import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  Select,
  useColorModeValue,
  Button,
  Alert,
  AlertIcon,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend)

const PERIOD_OPTIONS = [
  { value: "1", label: "Last 1 Month" },
  { value: "3", label: "Last 3 Months" },
  { value: "6", label: "Last 6 Months" },
  { value: "12", label: "Last 12 Months" },
]

const CostSales = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [byArticle, setByArticle] = useState([])
  const [trend, setTrend] = useState([])
  const [periodMonths, setPeriodMonths] = useState("3")
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const kpiBg = useColorModeValue("teal.50", "teal.900")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer, periodMonths])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const months = parseInt(periodMonths)
      const endDate = new Date()
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - months, 1)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/cost-sales`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
          startDate: fmt(startDate),
          endDate: fmt(endDate),
        },
        { signal }
      )
      if (res.data.status === 1) {
        setByArticle(res.data.data.byArticle || [])
        setTrend(res.data.data.trend || [])
      } else {
        setByArticle([])
        setTrend([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("CostSales fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const formatNum = (val) => (parseInt(val) || 0).toLocaleString()

  // Totals
  const totals = (() => {
    let revenue = 0, cost = 0, units = 0
    let articlesWithCost = 0
    byArticle.forEach((r) => {
      revenue += parseFloat(r.total_revenue) || 0
      cost += parseFloat(r.total_cost) || 0
      units += parseInt(r.total_units) || 0
      if (r.has_cost) articlesWithCost++
    })
    const gp = revenue - cost
    const gpPercent = revenue > 0 ? (gp / revenue) * 100 : 0
    return { revenue, cost, gp, gpPercent, units, articlesWithCost }
  })()

  const missingCostCount = byArticle.length - totals.articlesWithCost

  // Chart data
  const chartData = (() => {
    if (!trend.length) return null
    const labels = trend.map((r) => `${r.month_name} ${String(r.year).slice(-2)}`)
    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Revenue",
          data: trend.map((r) => parseFloat(r.total_revenue) || 0),
          backgroundColor: "rgba(52, 211, 153, 0.7)",
          borderColor: "rgba(52, 211, 153, 1)",
          borderWidth: 1,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Cost",
          data: trend.map((r) => parseFloat(r.total_cost) || 0),
          backgroundColor: "rgba(191, 63, 56, 0.7)",
          borderColor: "rgba(191, 63, 56, 1)",
          borderWidth: 1,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line",
          label: "GP %",
          data: trend.map((r) => parseFloat(r.gp_percent) || 0),
          borderColor: "rgba(59, 130, 246, 1)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          borderWidth: 2,
          pointRadius: 4,
          pointBackgroundColor: "rgba(59, 130, 246, 1)",
          tension: 0.3,
          yAxisID: "y1",
          order: 1,
        },
      ],
    }
  })()

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y
            if (ctx.dataset.label === "GP %") return `GP %: ${val.toFixed(1)}%`
            return `${ctx.dataset.label}: R ${val.toLocaleString()}`
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: "left",
        ticks: { callback: (v) => `R ${(v / 1000).toFixed(0)}k` },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: { drawOnChartArea: false },
        max: 100,
        ticks: { callback: (v) => `${v}%` },
      },
    },
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()

      // Article sheet
      const ws1 = workbook.addWorksheet("Cost by Article")
      ws1.columns = [
        { header: "Article Code", key: "article_code", width: 15 },
        { header: "Description", key: "articledescription", width: 35 },
        { header: "Brand", key: "brand", width: 18 },
        { header: "Unit Cost", key: "unit_cost", width: 12 },
        { header: "Units Sold", key: "total_units", width: 12 },
        { header: "Revenue", key: "total_revenue", width: 15 },
        { header: "Cost", key: "total_cost", width: 15 },
        { header: "Gross Profit", key: "gross_profit", width: 15 },
        { header: "GP %", key: "gp_percent", width: 10 },
      ]
      byArticle.forEach((row) => {
        ws1.addRow({
          article_code: row.article_code,
          articledescription: row.articledescription,
          brand: row.brand || "-",
          unit_cost: parseFloat(row.unit_cost) || 0,
          total_units: parseInt(row.total_units) || 0,
          total_revenue: parseFloat(row.total_revenue) || 0,
          total_cost: parseFloat(row.total_cost) || 0,
          gross_profit: parseFloat(row.gross_profit) || 0,
          gp_percent: parseFloat(row.gp_percent) || 0,
        })
      })
      ws1.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
      })
      ws1.getColumn("total_revenue").numFmt = "#,##0.00"
      ws1.getColumn("total_cost").numFmt = "#,##0.00"
      ws1.getColumn("gross_profit").numFmt = "#,##0.00"

      // Trend sheet
      if (trend.length > 0) {
        const ws2 = workbook.addWorksheet("Monthly Trend")
        ws2.columns = [
          { header: "Year", key: "year", width: 8 },
          { header: "Month", key: "month_name", width: 10 },
          { header: "Units", key: "total_units", width: 12 },
          { header: "Revenue", key: "total_revenue", width: 15 },
          { header: "Cost", key: "total_cost", width: 15 },
          { header: "Gross Profit", key: "gross_profit", width: 15 },
          { header: "GP %", key: "gp_percent", width: 10 },
        ]
        trend.forEach((row) => {
          ws2.addRow({
            year: row.year,
            month_name: row.month_name,
            total_units: parseInt(row.total_units) || 0,
            total_revenue: parseFloat(row.total_revenue) || 0,
            total_cost: parseFloat(row.total_cost) || 0,
            gross_profit: parseFloat(row.gross_profit) || 0,
            gp_percent: parseFloat(row.gp_percent) || 0,
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Cost_Sales_${supplier}_${retailer}_${periodMonths}m.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const hasData = byArticle.length > 0

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>Period</Text>
          <Select value={periodMonths} onChange={(e) => setPeriodMonths(e.target.value)} size="sm" w="160px" bg={cardBg}>
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </Box>
        {hasData && (
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="teal" onClick={handleDownload} isLoading={downloading} loadingText="Downloading">
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="xl" color="teal.500" /></Flex>
      ) : !hasData ? (
        <Box textAlign="center" py={10}><Text color="gray.500">No sales data found for this period</Text></Box>
      ) : (
        <>
          {/* Missing cost warning */}
          {missingCostCount > 0 && (
            <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              {missingCostCount} of {byArticle.length} articles have no cost entry in the margin table. Their GP% shows 100% — upload margin data for accurate GP calculations.
            </Alert>
          )}

          {/* KPI Cards */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Revenue</StatLabel>
              <StatNumber fontSize="lg" color="green.600">{formatCurrency(totals.revenue)}</StatNumber>
              <Text fontSize="xs" color="gray.500">{formatNum(totals.units)} units</Text>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Cost of Sales</StatLabel>
              <StatNumber fontSize="lg" color="red.500">{formatCurrency(totals.cost)}</StatNumber>
              <Text fontSize="xs" color="gray.500">{totals.articlesWithCost}/{byArticle.length} with cost</Text>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Gross Profit</StatLabel>
              <StatNumber fontSize="lg" color="teal.600">{formatCurrency(totals.gp)}</StatNumber>
            </Stat>
            <Stat bg={kpiBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">GP %</StatLabel>
              <StatNumber fontSize="lg" color={totals.gpPercent >= 20 ? "green.600" : "orange.500"}>
                {totals.gpPercent.toFixed(1)}%
              </StatNumber>
            </Stat>
          </SimpleGrid>

          {/* Chart */}
          {chartData && trend.length > 0 && (
            <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} p={4} mb={4}>
              <Heading size="sm" mb={3}>Revenue / Cost / GP% Trend</Heading>
              <Box h="300px">
                <Bar data={chartData} options={chartOptions} />
              </Box>
            </Box>
          )}

          {/* By-article table */}
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
            <Box p={3} borderBottom="1px" borderColor={borderColor}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="sm">Article Breakdown</Heading>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Revenue, cost, and GP% per article — sorted by revenue. Orange GP% indicates below 20%.
                  </Text>
                </Box>
                <Badge colorScheme="purple">{byArticle.length}</Badge>
              </Flex>
            </Box>
            <Box maxH="500px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Article</Th>
                    <Th>Brand</Th>
                    <Th isNumeric>Units</Th>
                    <Th isNumeric>Unit Cost</Th>
                    <Th isNumeric>Revenue</Th>
                    <Th isNumeric>Cost</Th>
                    <Th isNumeric>GP</Th>
                    <Th isNumeric>GP %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {byArticle.map((row, i) => {
                    const gp = parseFloat(row.gp_percent) || 0
                    const gpColor = !row.has_cost ? "gray.400" : gp < 15 ? "red.500" : gp < 20 ? "orange.500" : "green.600"
                    return (
                      <Tr key={i} _hover={{ bg: hoverBg }}>
                        <Td fontSize="xs" maxW="260px" isTruncated>{row.articledescription}</Td>
                        <Td fontSize="xs">{row.brand || "-"}</Td>
                        <Td isNumeric fontSize="xs">{formatNum(row.total_units)}</Td>
                        <Td isNumeric fontSize="xs">
                          {row.has_cost ? `R ${parseFloat(row.unit_cost).toFixed(2)}` : <Text as="span" color="gray.400" fontSize="2xs">no cost</Text>}
                        </Td>
                        <Td isNumeric fontSize="xs">{formatCurrency(row.total_revenue)}</Td>
                        <Td isNumeric fontSize="xs">{row.has_cost ? formatCurrency(row.total_cost) : "-"}</Td>
                        <Td isNumeric fontSize="xs">{row.has_cost ? formatCurrency(row.gross_profit) : "-"}</Td>
                        <Td isNumeric fontSize="xs" fontWeight="bold" color={gpColor}>
                          {row.has_cost ? `${gp.toFixed(1)}%` : "-"}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default CostSales
